import { Autocomplete, InputBase, Stack, Typography } from '@mui/material'
import { autocompleteClasses } from '@mui/material/Autocomplete'
import { inputBaseClasses } from '@mui/material/InputBase'
import _, { orderBy } from 'lodash'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import { RxCross1 } from 'react-icons/rx'
import { useNavigate } from 'react-router'
import { useDebounce } from 'react-use'
import { tss } from 'tss-react/mui'
import { DataType, SearchQuery, useSearchLazyQuery } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { uiColors } from '~/common/styles/colors'
import { controlKey } from '~/common/utils/keymap'
import { retrieveRecentSearches, storeRecentSearch } from '~/common/utils/local-storage'
import { trackGlobalSearch } from '~/common/utils/metrics'
import {
  getMatchScore,
  getMatchedCell,
  getNoOptionsText,
  getOptionLabel,
  getSearchIconByOptionType,
  handleLongText,
  pushSelectedOptionToRouter,
  splitByQuery,
} from '~/common/utils/search'
import { SearchModalOpenType } from '~/components/navigation/SearchInput'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { SearchModal } from '../ui/molecules/modal/SearchModal'
import { SearchResultsOption } from './SearchResultsOption'
import { SearchResultsPopper } from './SearchResultsPopper'
import { SearchResult } from './types'

export type SearchHistoryResult = SearchResult & {
  timestamp: number
}

export type MatchedSearchResult = SearchHistoryResult & {
  matches?: number
  matchedColumnName?: string
  matchedColumnType?: DataType
  matchedCellData?: string
}

export type OrderedOption = MatchedSearchResult & { order: number }

interface SearchInputModalProps {
  onClose: () => void
  open: boolean
  openType: SearchModalOpenType
}

/*
 * Top level search bar input with autocomplete and search history
 */
export const SearchInputModal = ({ onClose, open, openType }: SearchInputModalProps) => {
  const navigate = useNavigate()
  const { t } = useTypedTranslation()
  const { classes } = useStyles()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('')
  const [searchTimerMessage, setSearchTimerMessage] = useState<string>('')
  const [lastSearchedQuery, setLastSearchedQuery] = useState<string>('')
  const [loadSearch, { loading }] = useSearchLazyQuery()
  const [searchData, setSearchData] = useState<SearchQuery | undefined>()
  const { user } = useGlobalContext()

  // Check if a valid search query has been entered, not just whitespace
  const isValid = searchQuery.trim().length > 0

  const handleClose = () => {
    setSearchQuery('')
    setSearchData(undefined)
    onClose()
  }

  // Function to handle when the input changes
  const onInputChange = (event: SyntheticEvent, value: string) => {
    setSearchQuery(value)
  }

  // Debounce the search query @ 200ms because a typical keystroke is about 150-300ms
  useDebounce(
    () => {
      if (searchQuery !== lastSearchedQuery) {
        setDebouncedSearchQuery(searchQuery)
      }
    },
    200,
    [searchQuery],
  )

  // Perform the search using the debounced query
  useEffect(() => {
    if (!isValid || debouncedSearchQuery === lastSearchedQuery) {
      return
    }

    // Clean search string of leading and trailing whitespace before querying
    const searchString = debouncedSearchQuery.trim()

    // Timing the search results to show the user, will be displayed in the popper
    const startTime = performance.now()

    loadSearch({
      variables: { searchString },
      // Also query the back-end in case the cache is stale
      fetchPolicy: 'cache-and-network',
    }).then((data) => {
      const endTime = performance.now()
      const searchDuration = (endTime - startTime) / 1000
      setSearchData(data.data)
      setLastSearchedQuery(debouncedSearchQuery)
      setSearchTimerMessage(searchDuration.toFixed(3))
    })
    trackGlobalSearch({ searchTerm: debouncedSearchQuery })
  }, [debouncedSearchQuery, isValid, lastSearchedQuery, loadSearch])

  const optionsWithMatches: MatchedSearchResult[] = useMemo(() => {
    if (!searchData || !isValid) {
      return []
    }

    // Consolidate all the results into a single array
    const rowResults = searchData.rows.rows
    const tableResults = searchData.tables.tables
    const documentResults = searchData.documents.documents
    const formResults = searchData.forms.forms
    const formV2Results = searchData.formsV2.formsV2

    const results = [
      ...rowResults,
      ...tableResults,
      ...documentResults,
      ...formResults,
      ...formV2Results,
    ]

    // Map over results to add a 'matches' property to each option, get matched cell data, and
    // set the timestamp for queried data to '0' since it's not from search history
    return results.map((option) => {
      const { matchedColumnName, matchedColumnType, matchedCellData } = getMatchedCell(
        option,
        searchQuery,
      )

      const matches = getMatchScore(option, searchQuery)
      return {
        ...option,
        matches,
        matchedColumnName,
        matchedColumnType,
        matchedCellData,
        timestamp: 0,
      }
    })
  }, [searchData, isValid, searchQuery])

  if (!user) {
    return null
  }

  // Boolean for determining if we're using live search results or showing search history
  const usingLiveSearch = optionsWithMatches.length > 0 || isValid
  const searchHistory = retrieveRecentSearches(user.id)

  // Order the live options or search history based on the number of matches to put the best options
  // at the top. Additionally, add an 'order' property for the .map keying in renderOption.
  const orderedOptions: OrderedOption[] = (
    usingLiveSearch ? orderBy(optionsWithMatches, ['matches'], ['desc']) : searchHistory
  ).map((option, index) => ({ ...option, order: index }))

  // Function to handle when an option is selected
  const handleOptionSelected = (
    event: SyntheticEvent<Element, Event>,
    option: SearchResult | null,
  ) => {
    if (_.isNil(option)) {
      return
    }

    // Push the selected option to the router and store the search in local storage
    pushSelectedOptionToRouter(option, navigate)
    storeRecentSearch(user.id, option)

    // Close the search modal, blur the search input, and reset the search query
    handleClose()
  }

  return (
    <SearchModal
      fullWidth
      onClose={handleClose}
      open={open}
      openedByKeyboard={openType === 'keyboard'}
    >
      <div className={classes.searchInputContainer}>
        <div className={classes.iconContainer}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <LoadingSpinner type="search" />
            </div>
          ) : (
            <FiSearch />
          )}
        </div>
        <Autocomplete
          PopperComponent={(props) => {
            if (!usingLiveSearch && !orderedOptions.length) {
              return null
            }
            return (
              <Stack className={classes.autoCompletePopper}>
                {usingLiveSearch ? (
                  <Typography className={classes.responseText} variant="body1">
                    {orderedOptions.length} {t('search.results_in')} ({searchTimerMessage}s)
                  </Typography>
                ) : (
                  <div className={classes.responseTextWrapper}>
                    <Typography className={classes.responseText} variant="body1">
                      {t('search.recent_searches')}
                    </Typography>
                  </div>
                )}
                <SearchResultsPopper {...props} />
              </Stack>
            )
          }}
          className={classes.autoComplete}
          clearOnBlur={false}
          clearOnEscape={true}
          filterOptions={(options) => options}
          getOptionLabel={getOptionLabel}
          id="command-bar"
          inputValue={searchQuery}
          noOptionsText={getNoOptionsText(t, loading, searchQuery)}
          onChange={handleOptionSelected}
          onInputChange={onInputChange}
          open={!!open}
          openOnFocus
          options={orderedOptions}
          popupIcon={null}
          renderInput={({ InputProps, ...params }) => (
            <InputBase
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleClose()
                }
              }}
              placeholder={open ? `Search (${controlKey('K')})` : ''}
              {...InputProps}
              {...params}
              className={classes.input}
              endAdornment={
                <div
                  aria-label="close search"
                  className={classes.closeButton}
                  onClick={handleClose}
                  role="button"
                >
                  <RxCross1 />
                </div>
              }
              fullWidth
            />
          )}
          renderOption={(props, option) => {
            const label = getOptionLabel(option)
            const IconComponent = getSearchIconByOptionType(option)

            // Split the label by the query, including the query itself in the result
            const labelParts = splitByQuery(label, searchQuery, usingLiveSearch)
            let matchedCellData = option.matchedCellData ?? ''
            matchedCellData = handleLongText(matchedCellData, searchQuery, usingLiveSearch)

            // Split the matched cell data by the query, including the query itself in the result
            const cellParts = splitByQuery(matchedCellData, searchQuery, usingLiveSearch)
            const hasCellTextMatch = option.matches === 0 && option.__typename === 'TableRow'
            return (
              <SearchResultsOption
                cellParts={cellParts}
                hasCellTextMatch={hasCellTextMatch}
                iconComponent={IconComponent}
                labelParts={labelParts}
                props={props}
                usingLiveSearch={usingLiveSearch}
                {...props}
                option={option}
                searchQuery={searchQuery}
              />
            )
          }}
          value={null}
        />
      </div>
    </SearchModal>
  )
}

const useStyles = tss.withName('SearchInputModal').create(({ theme }) => ({
  searchInputContainer: {
    width: '100%',
    borderBottom: `1px solid ${theme.palette.common.doss.gray[4]}`,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    borderRadius: '4px',
    gap: 0,
  },
  iconContainer: {
    paddingBlock: theme.spacing(1.5),
    paddingInline: theme.spacing(2),
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: theme.palette.common.doss.gray[10],
    zIndex: 1,
    fontSize: 'large',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(0.25),
    marginInlineEnd: theme.spacing(0),
    marginBottom: theme.spacing(0.25),
    marginInlineStart: theme.spacing(0.25),
    color: theme.palette.common.doss.gray[10],
  },
  autoComplete: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    [`& .${autocompleteClasses.hasPopupIcon}`]: {
      paddingInlineEnd: 0,
    },
    [`&& .${autocompleteClasses.inputRoot}`]: {
      paddingInlineEnd: '8px',
    },
    minWidth: 350,
  },
  autoCompletePopper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    position: 'absolute',
    top: '42px',
    left: '0px',
    width: '100%',
    height: 'calc(100% - 48px)',
    overflow: 'hidden',
    padding: theme.spacing(1),
  },
  input: {
    [`& .${inputBaseClasses.input}`]: {
      paddingBlock: theme.spacing(1.5),
      transition: theme.transitions.create('width'),
      // vertical padding + font size from searchIcon
      paddingInline: `calc(1em + ${theme.spacing(4)})`,
      width: '100%',
      color: theme.palette.text.primary,
    },
    backgroundColor: theme.palette.common.doss.gray[2],
    borderRadius: 1,
    color: uiColors.content.primary,
    fontSize: '14px',
  },
  responseText: {
    paddingTop: theme.spacing(1),
    paddingInlineEnd: theme.spacing(1),
    paddingBottom: theme.spacing(0.25),
    paddingInlineStart: theme.spacing(1),
    color: theme.palette.common.doss.gray[8],
  },
  responseTextWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    paddingInline: '12px',
    cursor: 'pointer',
  },
}))
